import * as React from "react"
import { cn } from "@/lib/utils"

function formatWithCommas(value: string, decimals?: number): string {
  if (!value) return "";
  const cleaned = value.replace(/[^0-9.]/g, "");
  const parts = cleaned.split(".");
  const intPart = parts[0] || "";
  const formatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  if (parts.length > 1) {
    const decPart = decimals !== undefined ? parts[1].slice(0, decimals) : parts[1];
    return `${formatted}.${decPart}`;
  }
  return formatted;
}

function stripCommas(value: string): string {
  return value.replace(/,/g, "");
}

interface FormattedNumberInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange" | "value" | "type"> {
  value: string | number | null | undefined;
  onChange: (rawValue: string) => void;
  decimals?: number;
  isCurrency?: boolean;
}

const FormattedNumberInput = React.forwardRef<HTMLInputElement, FormattedNumberInputProps>(
  ({ className, value, onChange, decimals, isCurrency, placeholder, onBlur, ...props }, ref) => {
    const [displayValue, setDisplayValue] = React.useState(() => {
      const raw = value?.toString() ?? "";
      return formatWithCommas(raw, decimals);
    });

    React.useEffect(() => {
      const raw = value?.toString() ?? "";
      const stripped = stripCommas(displayValue);
      if (raw !== stripped) {
        setDisplayValue(formatWithCommas(raw, decimals));
      }
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target.value;
      const allowDecimals = decimals === undefined || decimals > 0;
      const cleaned = allowDecimals
        ? input.replace(/[^0-9.]/g, "")
        : input.replace(/[^0-9]/g, "");
      if (allowDecimals) {
        const dotCount = (cleaned.match(/\./g) || []).length;
        if (dotCount > 1) return;
      }
      const formatted = formatWithCommas(cleaned, decimals);
      setDisplayValue(formatted);
      onChange(stripCommas(formatted));
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      if (isCurrency && displayValue && !displayValue.includes(".")) {
        const withDecimals = displayValue + ".00";
        setDisplayValue(withDecimals);
        onChange(stripCommas(withDecimals));
      } else if (isCurrency && displayValue && displayValue.includes(".")) {
        const parts = displayValue.split(".");
        const decPart = (parts[1] || "").padEnd(2, "0").slice(0, 2);
        const fixed = `${parts[0]}.${decPart}`;
        setDisplayValue(fixed);
        onChange(stripCommas(fixed));
      }
      onBlur?.(e);
    };

    const inputMode = (decimals !== undefined && decimals === 0) ? "numeric" : "decimal";

    return (
      <input
        type="text"
        inputMode={inputMode}
        className={cn(
          "flex h-9 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          className
        )}
        ref={ref}
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        {...props}
      />
    );
  }
);
FormattedNumberInput.displayName = "FormattedNumberInput";

export { FormattedNumberInput };
